import React, { useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { Immunization, ImmunizationCreate, ImmunizationUpdate } from '../../types/immunization';
import { ImmunizationCatalogItem } from '../../types/immunization-catalog';
import { API_ENDPOINTS } from '../../config/api';
import { getImmunizationCatalog } from '../../utils/immunizationCatalogApi';
import { getPetSchedule } from '../../utils/immunizationScheduleApi';
import { getInventoryLots } from '../../utils/inventoryApi';
import { ImmunizationSchedule } from '../../types/immunization-schedule';
import { useAuth } from '../../contexts/AuthContext';

interface ImmunizationFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    petId: string;
    immunizationToEdit?: Immunization;
    scheduleId?: string;
    catalogItemId?: string;
    initialValues?: Partial<ImmunizationCreate>;
}

export const ImmunizationFormModal: React.FC<ImmunizationFormModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    petId,
    immunizationToEdit,
    scheduleId,
    catalogItemId,
    initialValues
}) => {
    const { user } = useAuth();
    const [catalogItems, setCatalogItems] = useState<ImmunizationCatalogItem[]>([]);
    const [showCatalogDropdown, setShowCatalogDropdown] = useState(false);
    const [catalogSearch, setCatalogSearch] = useState('');
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<ImmunizationCatalogItem | null>(null);
    const [stockOnHand, setStockOnHand] = useState<number | null>(null);
    const [stockLoading, setStockLoading] = useState(false);

    const [scheduledItems, setScheduledItems] = useState<ImmunizationSchedule[]>([]);
    const [selectedScheduleId, setSelectedScheduleId] = useState<string | undefined>(scheduleId);

    // Helper to get today's date in local timezone as YYYY-MM-DD
    const getLocalDateString = () => new Date().toLocaleDateString('en-CA');

    const [formData, setFormData] = useState<ImmunizationCreate>({
        name: '',
        site: '',
        manufacturer: '',
        lot_number: '',
        administered_date: getLocalDateString(),
        due_date: '',
        note: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen, onClose]);

    // Fetch catalog items when modal opens
    useEffect(() => {
        if (isOpen && user?.practice_id && !immunizationToEdit) {
            fetchCatalogItems();
            fetchScheduledItems();
        }
    }, [isOpen, user?.practice_id, immunizationToEdit]);

    const fetchScheduledItems = async () => {
        if (!petId) return;
        try {
            const schedule = await getPetSchedule(petId, false);
            // Combine overdue and upcoming, sort by date
            const items = [...schedule.overdue, ...schedule.upcoming].sort((a, b) =>
                new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime()
            );
            setScheduledItems(items);
        } catch (err) {
            console.error('Failed to fetch schedule:', err);
        }
    };

    const fetchCatalogItems = async () => {
        if (!user?.practice_id) return;

        try {
            const items = await getImmunizationCatalog(user.practice_id, undefined, true);
            setCatalogItems(items);
        } catch (err) {
            console.error('Failed to fetch catalog items:', err);
        }
    };

    // Auto-select catalog item if catalogItemId is provided (e.g. from Administer button)
    useEffect(() => {
        if (catalogItemId && catalogItems.length > 0 && !selectedCatalogItem) {
            const item = catalogItems.find(i => i.id === catalogItemId);
            if (item) {
                // Just set the selected item state so ID is sent and inventory logic runs.
                // We do NOT overwrite formData as it's already populated via initialValues.
                setSelectedCatalogItem(item);
            }
        }
    }, [catalogItemId, catalogItems, selectedCatalogItem]);

    const filteredCatalogItems = catalogItems.filter(item =>
        item.name.toLowerCase().includes(catalogSearch.toLowerCase())
    );

    const handleSelectCatalogItem = (item: ImmunizationCatalogItem) => {
        setSelectedCatalogItem(item);
        // Auto-fill form from catalog item
        const newFormData: any = {
            name: item.name,
            site: item.default_site || '',
            manufacturer: item.default_manufacturer || '',
            lot_number: formData.lot_number, // Keep existing lot number
            administered_date: formData.administered_date, // Keep existing date
            due_date: '',
            note: item.notes || ''
        };

        // Calculate due date if typical_duration_days is set
        if (item.typical_duration_days && formData.administered_date) {
            const adminDate = new Date(formData.administered_date + 'T00:00:00'); // Parse as local time
            adminDate.setDate(adminDate.getDate() + item.typical_duration_days);
            newFormData.due_date = adminDate.toLocaleDateString('en-CA');
        }

        setFormData(newFormData);
        setShowCatalogDropdown(false);
        setCatalogSearch('');
        setSelectedScheduleId(undefined); // Clear schedule selection if catalog item is picked
    };

    const handleSelectScheduledItem = (item: ImmunizationSchedule) => {
        setSelectedCatalogItem(null);
        setFormData({
            ...formData,
            name: item.immunization_name,
            note: item.notes || '',
            administered_date: getLocalDateString()
        });
        setSelectedScheduleId(item.id);
        // Don't close anything, just select it visually
    };

    // If the chosen catalog item is stock-tracked, show on-hand + "will consume" indicator
    useEffect(() => {
        const pid = user?.practice_id;
        if (!isOpen || !pid) return;
        if (!selectedCatalogItem || !selectedCatalogItem.is_stock_tracked) {
            setStockOnHand(null);
            setStockLoading(false);
            return;
        }

        let cancelled = false;
        (async () => {
            setStockLoading(true);
            try {
                const lots = await getInventoryLots(pid, {
                    catalog_item_id: selectedCatalogItem.id,
                    catalog_type: 'immunization',
                    include_consumed: false,
                });
                if (cancelled) return;
                const onHand = lots.reduce((sum, l) => sum + (Number(l.quantity_on_hand) || 0), 0);
                setStockOnHand(onHand);
            } catch {
                if (!cancelled) setStockOnHand(null);
            } finally {
                if (!cancelled) setStockLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, user?.practice_id, selectedCatalogItem]);

    useEffect(() => {
        if (immunizationToEdit) {
            setSelectedCatalogItem(null);
            setFormData({
                name: immunizationToEdit.name,
                site: immunizationToEdit.site || '',
                manufacturer: immunizationToEdit.manufacturer || '',
                lot_number: immunizationToEdit.lot_number || '',
                administered_date: immunizationToEdit.administered_date ? immunizationToEdit.administered_date.split('T')[0] : getLocalDateString(),
                due_date: immunizationToEdit.due_date ? immunizationToEdit.due_date.split('T')[0] : '',
                note: immunizationToEdit.note || ''
            });
        } else if (initialValues) {
            setFormData({
                name: initialValues.name || '',
                site: initialValues.site || '',
                manufacturer: initialValues.manufacturer || '',
                lot_number: initialValues.lot_number || '',
                administered_date: initialValues.administered_date || getLocalDateString(),
                due_date: initialValues.due_date || '',
                note: initialValues.note || ''
            });
        } else {
            // Reset form for new entry
            setFormData({
                name: '',
                site: '',
                manufacturer: '',
                lot_number: '',
                administered_date: getLocalDateString(),
                due_date: '',
                note: ''
            });
            setSelectedScheduleId(scheduleId);
            setSelectedCatalogItem(null);
        }
    }, [immunizationToEdit, isOpen, initialValues, scheduleId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            const payload = {
                ...formData,
                schedule_id: selectedScheduleId,
                catalog_item_id: selectedCatalogItem?.id,
                quantity_administered: 1,
                // Use noon local time to avoid UTC date shift issues
                // e.g., "2025-12-01" + "T12:00:00" prevents day-before display in US timezones
                administered_date: formData.administered_date
                    ? new Date(formData.administered_date + 'T12:00:00').toISOString()
                    : new Date().toISOString(),
                due_date: formData.due_date
                    ? new Date(formData.due_date + 'T12:00:00').toISOString()
                    : null
            };

            let response;
            if (immunizationToEdit) {
                response = await fetch(API_ENDPOINTS.IMMUNIZATIONS.UPDATE(immunizationToEdit.id), {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(payload)
                });
            } else {
                response = await fetch(API_ENDPOINTS.IMMUNIZATIONS.CREATE(petId), {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(payload)
                });
            }

            if (!response.ok) {
                throw new Error('Failed to save immunization');
            }

            onSuccess();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {immunizationToEdit
                            ? 'Edit Immunization'
                            : scheduleId
                                ? 'Mark as Administered'
                                : 'New Immunization'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
                    {error && (
                        <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Only show pending doses list if NO specific schedule was passed in */}
                    {!immunizationToEdit && !scheduleId && scheduledItems.length > 0 && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Pending Scheduled Doses
                            </label>
                            <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                                {scheduledItems.map(item => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => handleSelectScheduledItem(item)}
                                        className={`w-full flex items-center justify-between p-2 rounded-md text-left transition-colors ${selectedScheduleId === item.id
                                            ? 'bg-blue-100 border-blue-200 text-blue-800'
                                            : 'hover:bg-white text-gray-700'
                                            }`}
                                    >
                                        <div>
                                            <div className="font-medium text-sm">{item.immunization_name}</div>
                                            <div className="text-xs opacity-75">
                                                Due: {new Date(item.scheduled_date).toLocaleDateString()}
                                            </div>
                                        </div>
                                        {selectedScheduleId === item.id && (
                                            <div className="text-blue-600 text-xs font-bold px-2">Selected</div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Catalog Dropdown - Only show if not marking a specific scheduled dose */}
                    {!immunizationToEdit && !scheduleId && (
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Select from Catalog (optional)
                            </label>
                            <button
                                type="button"
                                onClick={() => setShowCatalogDropdown(!showCatalogDropdown)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            >
                                <span className="text-gray-500">Choose an immunization template...</span>
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>

                            {showCatalogDropdown && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    <div className="p-2 border-b border-gray-200">
                                        <input
                                            type="text"
                                            placeholder="Search catalog..."
                                            value={catalogSearch}
                                            onChange={(e) => setCatalogSearch(e.target.value)}
                                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {filteredCatalogItems.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-gray-500">No items found</div>
                                        ) : (
                                            filteredCatalogItems.map((item) => (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    onClick={() => handleSelectCatalogItem(item)}
                                                    className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 outline-none transition-colors"
                                                >
                                                    <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                                    {(item.default_site || item.typical_duration_days) && (
                                                        <div className="text-xs text-gray-500 mt-0.5">
                                                            {item.default_site && <span>Site: {item.default_site}</span>}
                                                            {item.default_site && item.typical_duration_days && <span> • </span>}
                                                            {item.typical_duration_days && <span>{item.typical_duration_days} days</span>}
                                                        </div>
                                                    )}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedCatalogItem?.is_stock_tracked && (
                                <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="font-medium">Inventory will be consumed</div>
                                        <div className="text-xs">
                                            On hand:{' '}
                                            {stockLoading ? 'Loading…' : (stockOnHand ?? '—')}
                                        </div>
                                    </div>
                                    <div className="text-xs text-blue-800 mt-1">
                                        Administering will consume 1 dose (on-hand can go negative if out of stock).
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Immunization Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            placeholder="e.g. Rabies, Bordetella"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Administered Date *</label>
                        <input
                            type="date"
                            required
                            value={formData.administered_date}
                            onChange={e => setFormData({ ...formData, administered_date: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        />
                    </div>

                    {/* Only show Due Date when creating new (not marking scheduled) */}
                    {!scheduleId && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                            <input
                                type="date"
                                value={formData.due_date || ''}
                                onChange={e => setFormData({ ...formData, due_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
                            <input
                                type="text"
                                value={formData.site || ''}
                                onChange={e => setFormData({ ...formData, site: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                placeholder="e.g. RF, LH"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                            <input
                                type="text"
                                value={formData.manufacturer || ''}
                                onChange={e => setFormData({ ...formData, manufacturer: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Lot Number</label>
                        <input
                            type="text"
                            value={formData.lot_number || ''}
                            onChange={e => setFormData({ ...formData, lot_number: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                            value={formData.note || ''}
                            onChange={e => setFormData({ ...formData, note: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                        />
                    </div>

                </form>

                <div className="flex justify-end gap-3 p-4 border-t border-gray-100 flex-shrink-0 bg-white">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Saving...' : 'Save Immunization'}
                    </button>
                </div>
            </div>
        </div>
    );
};
